import {
  storageGetAuth,
  storageIsNewAuth,
  storageSetAuth,
  storageSetCards,
  storageSetPreferredPayment,
} from './utils/storage';
import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import Cookies from 'js-cookie';

export const useCookieAuth = () => {
  return true;
};

export type User = {
  access_token: string;
  refresh_token: string;
};

type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  user: User | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  let tokens = null;
  if (useCookieAuth()) {
    const cookie = Cookies.get('auth');
    if (cookie) {
      const dataParts = cookie.split(';');
      if (dataParts.length >= 2) {
        tokens = {
          access_token: dataParts[0],
          refresh_token: dataParts[1],
        };
        if (storageIsNewAuth(tokens)) {
          storageSetAuth(tokens);
          storageSetCards(null);
        }
      }
    }
  } else {
    tokens = storageGetAuth();
  }
  let initAuth = false;
  if (tokens) {
    initAuth = true;
  }

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(initAuth);
  const [isLoading, setIsLoading] = useState<boolean>(!initAuth);
  const [user, setUser] = useState<User | null>(tokens);

  const login = () => {
    const tokens = storageGetAuth();
    if (tokens) {
      setIsAuthenticated(true);
      setIsLoading(false);
      setUser(tokens);
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setIsLoading(true);
    storageSetCards(null);
    storageSetPreferredPayment(null);
    if (useCookieAuth()) {
      Cookies.remove('auth');
      storageSetAuth(null);
    } else {
      storageSetAuth(null);
    }
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
    }),
    [user, isAuthenticated, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useEnhancedAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useEnhancedAuth must be used within a AuthProvider');
  }
  return context;
};

export default useEnhancedAuth;
